import React, { useState } from 'react';
import Button from '../../ui/Button';
import { ConfirmModal } from '../../ui/Modal';
import useMigrationSSE from './hooks/useMigrationSSE';
import { formatBytes, formatTime } from './utils';
import axiosInstance from '../../api/axiosInstance';

export default function LiveProgressPanel({ migrationId, onClose, showNotification }) {
  const { status, userProgress, eta, connected, error } = useMigrationSSE(migrationId);
  const [showConfirm, setShowConfirm] = useState(false);
  const [stopping, setStopping] = useState(false);

  const isComplete = status?.jobStatus === 'completed';
  const isFailed = status?.jobStatus === 'failed';
  const isCancelled = status?.jobStatus === 'cancelled';
  const isDone = isComplete || isFailed || isCancelled;

  const handleStop = async () => {
    setStopping(true);
    try {
      await axiosInstance.post(`/api/business/email-migration/jobs/${migrationId}/stop`);
      showNotification?.('Migration stopped', 'success');
    } catch (err) {
      showNotification?.('Failed to stop migration', 'error');
    } finally {
      setStopping(false);
      setShowConfirm(false);
    }
  };

  const progress = status?.progress || 0;
  const completedUsers = status?.completedUsers || 0;
  const totalUsers = status?.totalUsers || 0;
  const transferredData = status?.transferredData || 0;

  return (
    <div className="space-y-4 p-1">
      {/* Connection Indicator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
          <span className="text-xs text-gray-500">
            {connected ? 'Live' : error || 'Reconnecting...'}
          </span>
        </div>
        {eta && !isDone && (
          <span className="text-xs text-gray-500">
            ~{formatTime(eta.estimatedSecondsRemaining)} remaining
            {eta.avgBytesPerSecond > 0 && ` | ${formatBytes(eta.avgBytesPerSecond)}/s`}
          </span>
        )}
      </div>

      {/* Overall Progress */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Overall Progress</span>
          <span className="text-2xl font-bold text-gray-900 dark:text-white">{progress}%</span>
        </div>
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${
              isComplete ? 'bg-emerald-500' :
              isFailed ? 'bg-red-500' :
              isCancelled ? 'bg-amber-500' :
              'bg-gradient-to-r from-orange-400 to-orange-600 animate-pulse'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{completedUsers}/{totalUsers} users</span>
          <span>{formatBytes(transferredData)} transferred</span>
        </div>
      </div>

      {/* Completion Card */}
      {isDone && (
        <div className={`p-4 rounded-xl border ${
          isComplete ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' :
          isFailed ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' :
          'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            {isComplete ? (
              <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            ) : isFailed ? (
              <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            ) : (
              <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            )}
            <span className={`text-sm font-semibold ${
              isComplete ? 'text-emerald-700 dark:text-emerald-400' :
              isFailed ? 'text-red-700 dark:text-red-400' :
              'text-amber-700 dark:text-amber-400'
            }`}>
              {isComplete ? 'Migration Completed' : isFailed ? 'Migration Failed' : 'Migration Cancelled'}
            </span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {completedUsers}/{totalUsers} users migrated | {formatBytes(transferredData)} transferred
          </p>
          <Button variant="secondary" size="sm" onClick={onClose} className="mt-3">
            Close
          </Button>
        </div>
      )}

      {/* Per-User Progress */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">User Progress</h4>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {Object.entries(userProgress).length === 0 ? (
            <p className="text-xs text-gray-400 py-4 text-center">
              {isDone ? 'No user details available' : 'Waiting for migration to process users...'}
            </p>
          ) : (
            Object.entries(userProgress).map(([userId, up]) => (
              <div key={userId} className="flex items-center gap-3 p-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                {/* Status Icon */}
                {up.status === 'running' ? (
                  <svg className="w-4 h-4 text-orange-500 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} className="opacity-25" /><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" /></svg>
                ) : up.status === 'completed' ? (
                  <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                ) : (
                  <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{userId}</p>
                  {up.status === 'completed' && (
                    <p className="text-xs text-gray-400">
                      {up.emailCount} emails | {formatBytes(up.transferredBytes)}
                    </p>
                  )}
                  {up.status === 'failed' && (
                    <p className="text-xs text-red-500 truncate">{up.error}</p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Cancel Button */}
      {!isDone && (
        <Button variant="danger" size="sm" onClick={() => setShowConfirm(true)} disabled={stopping}>
          Cancel Migration
        </Button>
      )}

      <ConfirmModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleStop}
        title="Cancel Migration?"
        message="This will stop the migration. Already migrated users will not be affected."
        confirmText="Stop Migration"
        variant="danger"
        loading={stopping}
      />
    </div>
  );
}
